import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { ArrowRight, Check, X, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", invite_code: "" });
  const [restaurantPreview, setRestaurantPreview] = useState(null);
  const [checking, setChecking] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [showPw, setShowPw] = useState(false);

  const lookupCode = async (code) => {
    const trimmed = code.trim().toUpperCase();
    if (trimmed.length < 4) { setRestaurantPreview(null); return; }
    setChecking(true);
    try {
      const { data } = await api.get(`/public/restaurants/lookup`, { params: { code: trimmed } });
      setRestaurantPreview(data);
    } catch {
      setRestaurantPreview("invalid");
    } finally { setChecking(false); }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!restaurantPreview || restaurantPreview === "invalid") {
      setErr("Voer eerst een geldige deelcode in");
      return;
    }
    setLoading(true);
    try {
      await register({ ...form, role: "chef", invite_code: form.invite_code.trim().toUpperCase() });
      toast.success(`Welkom bij ${restaurantPreview.name}!`);
      nav("/");
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-10 bg-white" data-testid="register-page">
      <div className="w-full max-w-sm">
        <img src="/stockly-logo-tight.png" alt="Stockly" className="w-52 sm:w-64 md:w-[340px] h-auto mb-6 sm:mb-8" />

        <div className="overline mb-2">Registreren als chef</div>
        <h1 className="text-4xl font-bold tracking-tight mb-3 text-[#1B2D5C]">Sluit aan bij je restaurant.</h1>
        <p className="text-zinc-500 mb-8 text-sm">Vraag de deelcode aan je beheerder.</p>

        <form onSubmit={onSubmit} className="space-y-4" data-testid="register-form">
          <div>
            <label className="overline block mb-2">Deelcode van restaurant</label>
            <input
              type="text" required maxLength={8}
              value={form.invite_code}
              onChange={(e) => {
                const v = e.target.value.toUpperCase();
                setForm({ ...form, invite_code: v });
                lookupCode(v);
              }}
              placeholder="Bv. AB7CDE"
              className="w-full h-11 px-3 border border-zinc-300 rounded-md text-sm uppercase tracking-[0.2em] font-mono focus:outline-none focus:ring-2 focus:ring-[#5BC044]/45 focus:border-[#5BC044]"
              data-testid="register-invite-code"
            />
            {checking && <div className="text-xs text-zinc-500 mt-1.5">Controleren…</div>}
            {restaurantPreview && restaurantPreview !== "invalid" && (
              <div className="mt-2 flex items-center gap-2 text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2" data-testid="restaurant-preview">
                <Check size={14} /> <span><b>{restaurantPreview.name}</b> · {restaurantPreview.location}</span>
              </div>
            )}
            {restaurantPreview === "invalid" && form.invite_code.length >= 4 && (
              <div className="mt-2 flex items-center gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2" data-testid="restaurant-invalid">
                <X size={14} /> Ongeldige deelcode
              </div>
            )}
          </div>

          <div>
            <label className="overline block mb-2">Naam</label>
            <input type="text" required value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="w-full h-11 px-3 border border-zinc-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#5BC044]/45 focus:border-[#5BC044]"
              data-testid="register-name-input"
            />
          </div>
          <div>
            <label className="overline block mb-2">E-mail</label>
            <input type="email" required value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="w-full h-11 px-3 border border-zinc-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#5BC044]/45 focus:border-[#5BC044]"
              data-testid="register-email-input"
            />
          </div>
          <div>
            <label className="overline block mb-2">Wachtwoord</label>
            <div className="relative">
              <input type={showPw ? "text" : "password"} required minLength={6} value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                className="w-full h-11 pl-3 pr-11 border border-zinc-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#5BC044]/45 focus:border-[#5BC044]"
                data-testid="register-password-input"
              />
              <button type="button" onClick={() => setShowPw((v) => !v)} tabIndex={-1}
                className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 inline-flex items-center justify-center text-zinc-400 hover:text-[#1B2D5C] rounded"
                aria-label={showPw ? "Wachtwoord verbergen" : "Wachtwoord tonen"}
                data-testid="register-toggle-password"
              >
                {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>

          {err && <div className="text-sm text-red-600" data-testid="register-error">{err}</div>}

          <button type="submit" disabled={loading || !restaurantPreview || restaurantPreview === "invalid"}
            className="w-full h-11 bg-[#5BC044] hover:bg-[#4FAA3A] disabled:opacity-50 text-white rounded-md font-semibold inline-flex items-center justify-center gap-2"
            data-testid="register-submit-button"
          >
            {loading ? "Bezig..." : "Account aanmaken"}
            <ArrowRight size={16} />
          </button>
        </form>

        <div className="mt-6 text-sm text-zinc-500">
          Al een account? <Link to="/login" className="text-[#5BC044] font-medium hover:underline" data-testid="goto-login">Inloggen</Link>
        </div>
      </div>
    </div>
  );
}
